import PropTypes from 'prop-types';
import cn from 'classnames';
import * as animationFrame from 'dom-helpers/animationFrame';
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import memoize from 'memoize-one';
import { Scrollbars } from 'react-custom-scrollbars';
import * as dates from 'react-big-calendar/lib/utils/dates';
import DayColumn from 'react-big-calendar/lib/DayColumn';
import { notify } from 'react-big-calendar/lib/utils/helpers';
import { sortEvents } from 'react-big-calendar/lib/utils/eventLevels';
import { inRange } from '../../utils/inRange';
import Resources from 'react-big-calendar/lib/utils/Resources';
import { DayLayoutAlgorithmPropType } from 'react-big-calendar/lib/utils/propTypes';

import CustomTimeGridHeader from './CustomTimeGridHeader';

export default class CustomTimeGrid extends Component {
  constructor(props) {
    super(props);
    this.state = { gutterWidth: undefined, isOverflowing: null };
    this.scrollRef = React.createRef();
    this.contentRef = React.createRef();
    this._scrollRatio = null;
  }

  handleHorizontalMouseScroll() {
    const scrollbar = [...[...document.getElementsByClassName('custom-scrollbars')][0].children][0];
    const eventList = [...[...document.getElementsByClassName('custom-scrollbars')][0].children][0];

    function scrollHorizontally(e) {
      e = window.event || e;
      var delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
      scrollbar.scrollLeft -= delta * 40; // Multiplied by 40
      e.preventDefault();
    }

    if (eventList.addEventListener) {
      // IE9, Chrome, Safari, Opera
      eventList.addEventListener('mousewheel', scrollHorizontally, false);
      // Firefox
      eventList.addEventListener('DOMMouseScroll', scrollHorizontally, false);
    } else {
      // IE 6/7/8
      eventList.attachEvent('onmousewheel', scrollHorizontally);
    }
  }

  UNSAFE_componentWillMount() {
    this.calculateScroll();
  }

  componentDidMount() {
    this.checkOverflow();
    if (this.props.width == null) {
      this.measureGutter();
    }
    this.applyScroll();
    window.addEventListener('resize', this.handleResize);
  }

  handleScroll = (e) => {
    if (this.scrollRef.current) {
      this.scrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  handleResize = () => {
    animationFrame.cancel(this.rafHandle);
    this.rafHandle = animationFrame.request(this.checkOverflow);
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    animationFrame.cancel(this.rafHandle);
  }

  componentDidUpdate() {
    if (this.props.width == null) {
      this.measureGutter();
    }

    this.applyScroll();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { range, scrollToTime } = this.props;
    // When paginating, reset scroll
    if (
      !dates.eq(nextProps.range[0], range[0], 'minute') ||
      !dates.eq(nextProps.scrollToTime, scrollToTime, 'minute')
    ) {
      this.calculateScroll(nextProps);
    }
  }

  gutterRef = (ref) => {
    this.gutter = ref && findDOMNode(ref);
  };

  handleSelectAlldayEvent = (...args) => {
    //cancel any pending selections so only the event click goes through.
    this.clearSelection();
    notify(this.props.onSelectEvent, args);
  };

  handleSelectAllDaySlot = (slots, slotInfo) => {
    const { onSelectSlot } = this.props;
    notify(onSelectSlot, {
      slots,
      start: slots[0],
      end: slots[slots.length - 1],
      action: slotInfo.action,
    });
  };

  renderEvents(range, events, now) {
    let { min, max, components, accessors, localizer, dayLayoutAlgorithm } = this.props;

    const resources = this.memoizedResources(this.props.resources, accessors);
    const groupedEvents = resources.groupEvents(events);

    return resources.map(([id, resource], i) =>
      range.map((date, jj) => {
        let daysEvents = (groupedEvents.get(id) || []).filter((event) =>
          dates.inRange(date, accessors.start(event), accessors.end(event), 'day')
        );

        return (
          <DayColumn
            {...this.props}
            localizer={localizer}
            min={dates.merge(date, min)}
            max={dates.merge(date, max)}
            resource={resource && id}
            components={components}
            isNow={dates.eq(date, now, 'day')}
            key={i + '-' + jj}
            date={date}
            events={daysEvents}
            dayLayoutAlgorithm={dayLayoutAlgorithm}
          />
        );
      })
    );
  }

  render() {
    let {
      events,
      range,
      width,
      rtl,
      selected,
      getNow,
      resources,
      components,
      accessors,
      getters,
      localizer,
      employeesListRef,
      onListScroll,
      showMultiDayTimes,
      longPressThreshold,
      className,
    } = this.props;

    width = width || this.state.gutterWidth;

    let start = range[0],
      end = range[range.length - 1];

    this.slots = range.length;

    let currentEventsToDisplay = [],
      rangeEvents = [];

    events
      .map((event) => event.vacations)
      .flat()
      .forEach((event) => {
        if (inRange(event, start, end, accessors)) {
          let eStart = accessors.start(event),
            eEnd = accessors.end(event);

          if (
            accessors.allDay(event) ||
            (dates.isJustDate(eStart) && dates.isJustDate(eEnd)) ||
            (!showMultiDayTimes && !dates.eq(eStart, eEnd, 'day'))
          ) {
            currentEventsToDisplay.push(event);
          } else {
            rangeEvents.push(event);
          }
        }
      });

    currentEventsToDisplay.sort((a, b) => sortEvents(a, b, accessors));

    return (
      <div className={cn('rbc-time-view', 'vacation-calendar-view', className)}
      data-testid="calendar-view">
        {events.length ? (
          <>
            <Scrollbars
              onScroll={onListScroll}
              ref={employeesListRef}
              style={{ height: 639 }}
              className="custom-scrollbars"
            >
              <div className="vacation-calendar__header">
                {range.map((day) => {
                  return (
                    <div
                      key={day.getDate() + day.getMonth()}
                      className={cn(
                        'header-cell',
                        (day.getDay() === 0 || day.getDay() === 6) && 'header-cell--weekend',
                        dates.eq(day, getNow(), 'day') && 'header-cell_today'
                      )}
                    >
                      <div className={'header-cell__mount'}>{day.toString().split(' ')[0]}</div>
                      <div className={'header-cell__date'}>{`${day.getDate()} / ${day.getMonth() + 1 < 10 ? 0 : ''}${
                        day.getMonth() + 1
                      }`}</div>
                    </div>
                  );
                })}
              </div>
              <div className="employee-events-list" data-testid="employee-list">
                {events.map((employeeEvents) => {
                  return (
                    <CustomTimeGridHeader
                      key={employeeEvents.employeeId}
                      range={range}
                      className={`employee-events-row`}
                      events={employeeEvents.vacations.filter((event) =>
                        currentEventsToDisplay.find((ev) => ev.vacationId === event.vacationId)
                      )}
                      width={width}
                      rtl={rtl}
                      getNow={getNow}
                      localizer={localizer}
                      selected={selected}
                      resources={this.memoizedResources(resources, accessors)}
                      selectable={this.props.selectable}
                      accessors={accessors}
                      getters={getters}
                      components={components}
                      scrollRef={this.scrollRef}
                      isOverflowing={this.state.isOverflowing}
                      longPressThreshold={longPressThreshold}
                      onSelectSlot={this.handleSelectAllDaySlot}
                      onSelectEvent={this.handleSelectAlldayEvent}
                      onDoubleClickEvent={this.props.onDoubleClickEvent}
                      onDrillDown={this.props.onDrillDown}
                      getDrilldownView={this.props.getDrilldownView}
                    />
                  );
                })}
              </div>
            </Scrollbars>
          </>
        ) : (
          <div className="empty-employees-list">
            <span>No events for your search</span>
          </div>
        )}
        <div ref={this.contentRef} onScroll={this.handleScroll}></div>
      </div>
    );
  }

  clearSelection() {
    clearTimeout(this._selectTimer);
    this._pendingSelection = [];
  }

  measureGutter() {
    if (this.measureGutterAnimationFrameRequest) {
      // window.cancelAnimationFrame(this.measureGutterAnimationFrameRequest)
    }
    // this.measureGutterAnimationFrameRequest = window.requestAnimationFrame(
    // 	() => {
    // 		const width = getWidth(this.gutter)

    // 		if (width && this.state.gutterWidth !== width) {
    // 			this.setState({ gutterWidth: width })
    // 		}
    // 	}
    // )
  }

  applyScroll() {
    if (this._scrollRatio != null) {
      const content = this.contentRef.current;
      content.scrollTop = content.scrollHeight * this._scrollRatio;
      // Only do this once
      this._scrollRatio = null;
    }
  }

  calculateScroll(props = this.props) {
    const { min, max, scrollToTime } = props;

    const diffMillis = scrollToTime - dates.startOf(scrollToTime, 'day');
    const totalMillis = dates.diff(max, min);

    this._scrollRatio = diffMillis / totalMillis;
  }

  checkOverflow = () => {
    if (this._updatingOverflow) return;

    const content = this.contentRef.current;
    let isOverflowing = content.scrollHeight > content.clientHeight;

    if (this.state.isOverflowing !== isOverflowing) {
      this._updatingOverflow = true;
      this.setState({ isOverflowing }, () => {
        this._updatingOverflow = false;
      });
    }
  };

  memoizedResources = memoize((resources, accessors) => Resources(resources, accessors));
}

CustomTimeGrid.propTypes = {
  events: PropTypes.array.isRequired,
  resources: PropTypes.array,

  step: PropTypes.number,
  timeslots: PropTypes.number,
  range: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  getNow: PropTypes.func.isRequired,

  scrollToTime: PropTypes.instanceOf(Date),
  showMultiDayTimes: PropTypes.bool,

  rtl: PropTypes.bool,
  width: PropTypes.number,

  accessors: PropTypes.object.isRequired,
  components: PropTypes.object.isRequired,
  getters: PropTypes.object.isRequired,
  localizer: PropTypes.object.isRequired,

  selected: PropTypes.object,
  selectable: PropTypes.oneOf([true, false, 'ignoreEvents']),
  longPressThreshold: PropTypes.number,

  onNavigate: PropTypes.func,
  onSelectSlot: PropTypes.func,
  onSelectEnd: PropTypes.func,
  onSelectStart: PropTypes.func,
  onSelectEvent: PropTypes.func,
  onDoubleClickEvent: PropTypes.func,
  onDrillDown: PropTypes.func,
  getDrilldownView: PropTypes.func.isRequired,

  dayLayoutAlgorithm: DayLayoutAlgorithmPropType,
};

CustomTimeGrid.defaultProps = {
  step: 30,
  timeslots: 2,
  min: dates.startOf(new Date(Date.now()), 'day'),
  max: dates.endOf(new Date(Date.now()), 'day'),
  scrollToTime: dates.startOf(new Date(Date.now()), 'day'),
};
